import React, { useEffect, useCallback, useState } from 'react';
import {
    Box, Typography, Avatar, Skeleton, Chip, Button,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Tooltip, Divider, Tabs, Tab,
} from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SpeedIcon from '@mui/icons-material/Speed';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/es';

import {
    fetchSupervisionEntriesFromAPI,
    unassignClientInAPI,
    fetchResponseTimesFromAPI,
} from '../../Redux/SupervisionDucks';

const TIMES_PERIODS = [
    { label: 'Hoy', getDates: () => [dayjs().startOf('day'), dayjs().endOf('day')] },
    { label: '7 días', getDates: () => [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: '30 días', getDates: () => [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
];

dayjs.extend(utc);
dayjs.locale('es');

// ── Helpers ───────────────────────────────────────────────────────────────────

const getPerfil = () => new Cookies().get('perfil') ?? '';

const getUserBusinessLineIds = () => {
    const cookies = document.cookie.split('; ');
    const perfilCookie = cookies.find(c => c.startsWith('perfil='));
    const perfil = perfilCookie ? decodeURIComponent(perfilCookie.split('=')[1]) : '';
    if (perfil === 'Super-Admin') return null;
    const blCookie = cookies.find(c => c.startsWith('businessLineIds='));
    if (!blCookie) return [];
    return blCookie.split('=')[1].split(',').map(Number).filter(Boolean);
};

const fmtPhone = (tel) => {
    if (!tel) return '';
    const s = String(tel);
    return s.length > 4 ? `+(${s.substring(0, 2)}) ${s.substring(2)}` : s;
};

const getInitials = (name) => {
    if (!name?.trim()) return '??';
    return name.trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
};

// Semáforo de tiempo sin respuesta
const getSemaforoColor = (minutes) => {
    if (minutes < 0) return '#9e9e9e';   // sin dato
    if (minutes < 10) return '#22c55e';  // verde
    if (minutes <= 30) return '#f59e0b'; // amarillo
    return '#ef4444';                    // rojo
};

const fmtMinutes = (minutes) => {
    if (minutes < 0) return 'Sin datos';
    if (minutes === 0) return 'Ahora';
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// ── AgentGroup ────────────────────────────────────────────────────────────────

const AgentGroup = ({ agentName, agentUserId, entries, isSuperAdmin, onVer, onLiberar }) => {
    const initials = getInitials(agentName);

    return (
        <Box sx={{
            border: '1px solid #e5e7eb',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2,
        }}>
            {/* Cabecera del agente */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.5,
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e5e7eb',
            }}>
                <Avatar sx={{ bgcolor: '#7c3aed', width: 36, height: 36, fontSize: 13 }}>
                    {initials}
                </Avatar>
                <Box>
                    <Typography fontWeight={600} fontSize={14}>{agentName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {entries.length} conversación{entries.length !== 1 ? 'es' : ''} activa{entries.length !== 1 ? 's' : ''}
                    </Typography>
                </Box>
            </Box>

            {/* Filas de conversaciones */}
            {entries.map((entry, idx) => {
                const color = getSemaforoColor(entry.minutesWithoutResponse);
                const phone = fmtPhone(entry.clientPhone);

                return (
                    <Box key={entry.assignmentId}>
                        {idx > 0 && <Divider />}
                        <Box sx={{
                            display: 'flex', alignItems: 'center',
                            px: 2, py: 1.5, gap: 2,
                            '&:hover': { bgcolor: '#f5f5f5' },
                        }}>
                            {/* Info del cliente */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography fontWeight={600} fontSize={14} noWrap>
                                        {entry.clientName || 'Cliente sin nombre'}
                                    </Typography>
                                    {phone && (
                                        <Typography variant="caption" color="text.secondary">
                                            {phone}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                    <Chip
                                        label={entry.businessLineName || `BL ${entry.businessLineId}`}
                                        size="small"
                                        sx={{ fontSize: 11, height: 20 }}
                                    />
                                    <Chip
                                        label={entry.assignmentType === 'manual' ? 'Manual' : 'Automática'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: 11, height: 20 }}
                                    />
                                </Box>
                            </Box>

                            {/* Semáforo de tiempo */}
                            <Tooltip title={`Último mensaje del cliente hace ${fmtMinutes(entry.minutesWithoutResponse)}`}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                    <AccessTimeIcon sx={{ fontSize: 16, color }} />
                                    <Typography fontSize={13} fontWeight={500} sx={{ color }}>
                                        {fmtMinutes(entry.minutesWithoutResponse)}
                                    </Typography>
                                </Box>
                            </Tooltip>

                            {/* Acciones */}
                            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={() => onVer(entry)}
                                    sx={{ textTransform: 'none', fontSize: 12 }}
                                >
                                    Ver
                                </Button>
                                {isSuperAdmin && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<LockOpenIcon />}
                                        onClick={() => onLiberar(entry)}
                                        sx={{ textTransform: 'none', fontSize: 12 }}
                                    >
                                        Liberar
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

// ── SupervisionPage ───────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000;

const SupervisionPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { entries, loading, error, responseTimes, loadingTimes } = useSelector(s => s.supervision);

    const perfil = getPerfil();
    const isSuperAdmin = perfil === 'Super-Admin';
    const userBLIds = getUserBusinessLineIds(); // null = Super-Admin (all)

    const [lastUpdated, setLastUpdated] = useState(null);
    const [selectedBL, setSelectedBL] = useState(null);
    const [confirmEntry, setConfirmEntry] = useState(null); // entry a liberar
    const [view, setView] = useState(0); // 0 = en vivo, 1 = tiempos de respuesta
    const [timesPeriodIdx, setTimesPeriodIdx] = useState(0);

    // ── Carga ─────────────────────────────────────────────────────────────────
    const load = useCallback(() => {
        dispatch(fetchSupervisionEntriesFromAPI(selectedBL));
        setLastUpdated(dayjs().format('HH:mm'));
    }, [dispatch, selectedBL]);

    useEffect(() => {
        load();
        const interval = setInterval(load, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [load]);

    // ── Tiempos de respuesta ────────────────────────────────────────────────────
    const loadTimes = useCallback(() => {
        const [from, to] = TIMES_PERIODS[timesPeriodIdx].getDates();
        dispatch(fetchResponseTimesFromAPI(from.toDate(), to.toDate(), selectedBL));
    }, [dispatch, timesPeriodIdx, selectedBL]);

    useEffect(() => {
        if (view === 1) loadTimes();
    }, [view, loadTimes]);

    const filteredResponseTimes = (responseTimes ?? []).filter(t => {
        if (userBLIds !== null && !userBLIds.includes(t.businessLineId)) return false;
        if (selectedBL !== null && t.businessLineId !== selectedBL) return false;
        return true;
    });

    // ── Filtro por BL ─────────────────────────────────────────────────────────
    // Derivar BLs disponibles del perfil
    const availableBLs = entries
        .map(e => ({ id: e.businessLineId, name: e.businessLineName }))
        .filter((v, i, arr) => arr.findIndex(x => x.id === v.id) === i)
        .sort((a, b) => a.name.localeCompare(b.name));

    // Filtrar entradas según BL seleccionado y permisos del usuario
    const filteredEntries = entries.filter(e => {
        if (userBLIds !== null && !userBLIds.includes(e.businessLineId)) return false;
        if (selectedBL !== null && e.businessLineId !== selectedBL) return false;
        return true;
    });

    // Agrupar por agente
    const agentGroups = filteredEntries.reduce((acc, entry) => {
        const key = entry.agentUserId;
        if (!acc[key]) acc[key] = { agentName: entry.agentName, agentUserId: entry.agentUserId, entries: [] };
        acc[key].entries.push(entry);
        return acc;
    }, {});
    const groups = Object.values(agentGroups);

    // Contadores resumen
    const totalAgents = groups.length;
    const totalConversations = filteredEntries.length;

    // ── Acciones ──────────────────────────────────────────────────────────────
    const handleVer = (entry) => {
        navigate('/smart_talking/conversaciones', { state: { conversationId: entry.conversationId } });
    };

    const handleLiberar = async () => {
        if (!confirmEntry) return;
        try {
            await dispatch(unassignClientInAPI(confirmEntry.clientId, confirmEntry.businessLineId));
            setConfirmEntry(null);
            load();
        } catch {
            // error ya logueado en el duck
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: '#fff' }}>
            {/* Header */}
            <Box sx={{
                px: 2.5, py: 1.5,
                borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
            }}>
                <Typography variant="h6" fontWeight={700}>Supervisión</Typography>

                {/* Filtro BL */}
                {availableBLs.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', ml: 1 }}>
                        <Chip
                            label="Todos"
                            size="small"
                            onClick={() => setSelectedBL(null)}
                            color={selectedBL === null ? 'primary' : 'default'}
                            sx={{ fontSize: 12 }}
                        />
                        {availableBLs
                            .filter(bl => userBLIds === null || userBLIds.includes(bl.id))
                            .map(bl => (
                                <Chip
                                    key={bl.id}
                                    label={bl.name}
                                    size="small"
                                    onClick={() => setSelectedBL(bl.id)}
                                    color={selectedBL === bl.id ? 'primary' : 'default'}
                                    sx={{ fontSize: 12 }}
                                />
                            ))}
                    </Box>
                )}

                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {lastUpdated && (
                        <Typography variant="caption" color="text.secondary">
                            Actualizado: {lastUpdated}
                        </Typography>
                    )}
                    <Button size="small" onClick={load} sx={{ textTransform: 'none', fontSize: 12 }}>
                        Actualizar
                    </Button>
                </Box>
            </Box>

            {/* Tabs vista */}
            <Box sx={{ px: 2.5, borderBottom: '1px solid #e5e7eb' }}>
                <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ minHeight: 40 }}>
                    <Tab icon={<SupervisorAccountIcon sx={{ fontSize: 16 }} />} iconPosition="start"
                        label="En vivo" sx={{ textTransform: 'none', minHeight: 40, fontSize: 13 }} />
                    <Tab icon={<SpeedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
                        label="Tiempos de respuesta" sx={{ textTransform: 'none', minHeight: 40, fontSize: 13 }} />
                </Tabs>
            </Box>

            {/* Vista: tiempos de respuesta */}
            {view === 1 && (
                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 2, alignItems: 'center' }}>
                        {TIMES_PERIODS.map((p, i) => (
                            <Chip key={p.label} label={p.label} size="small"
                                onClick={() => setTimesPeriodIdx(i)}
                                color={timesPeriodIdx === i ? 'primary' : 'default'}
                                sx={{ fontSize: 12 }} />
                        ))}
                        <Button size="small" onClick={loadTimes} sx={{ ml: 'auto', textTransform: 'none', fontSize: 12 }}>
                            Actualizar
                        </Button>
                    </Box>

                    {loadingTimes ? (
                        [1, 2, 3].map(i => <Skeleton key={i} height={48} sx={{ mb: 1 }} />)
                    ) : filteredResponseTimes.length === 0 ? (
                        <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
                            <SpeedIcon sx={{ fontSize: 48, opacity: 0.25 }} />
                            <Typography variant="body2">Sin datos de tiempos en el período</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 1,
                                px: 2, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                                {['Consultor', 'Prom. resp.', '1ª resp.', 'Máx.', 'Pendientes'].map(h => (
                                    <Typography key={h} variant="caption" fontWeight={700} color="text.secondary"
                                        sx={{ fontSize: 11, textTransform: 'uppercase' }}>{h}</Typography>
                                ))}
                            </Box>
                            {filteredResponseTimes.map((t, idx) => (
                                <Box key={t.agentUserId}>
                                    {idx > 0 && <Divider />}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 1,
                                        px: 2, py: 1.25, alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ bgcolor: '#7c3aed', width: 30, height: 30, fontSize: 12 }}>
                                                {getInitials(t.agentName)}
                                            </Avatar>
                                            <Typography fontSize={13} fontWeight={600} noWrap>
                                                {t.agentName || `Agente ${t.agentUserId}`}
                                            </Typography>
                                        </Box>
                                        <Typography fontSize={13} fontWeight={600} sx={{ color: getSemaforoColor(t.avgResponseMinutes) }}>
                                            {fmtMinutes(Math.round(t.avgResponseMinutes))}
                                        </Typography>
                                        <Typography fontSize={13}>{fmtMinutes(Math.round(t.firstResponseAvgMinutes))}</Typography>
                                        <Typography fontSize={13}>{fmtMinutes(Math.round(t.maxResponseMinutes))}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Chip label={t.pendingNowCount} size="small"
                                                sx={{ height: 20, fontSize: 11, fontWeight: 700,
                                                    bgcolor: t.pendingNowCount > 0 ? '#fee2e2' : '#f3f4f6',
                                                    color: t.pendingNowCount > 0 ? '#dc2626' : '#6b7280' }} />
                                            {t.pendingNowCount > 0 && t.oldestPendingMinutes > 0 && (
                                                <Typography variant="caption" color="text.secondary">
                                                    ({fmtMinutes(t.oldestPendingMinutes)})
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* Resumen */}
            {view === 0 && !loading && !error && (
                <Box sx={{ px: 3, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="body2" color="text.secondary">
                        {totalAgents} agente{totalAgents !== 1 ? 's' : ''} activo{totalAgents !== 1 ? 's' : ''} ·{' '}
                        {totalConversations} conversación{totalConversations !== 1 ? 'es' : ''} en control humano
                    </Typography>
                </Box>
            )}

            {/* Contenido en vivo */}
            {view === 0 && (
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                {/* Skeleton */}
                {loading && (
                    <Box>
                        {[1, 2, 3].map(i => (
                            <Box key={i} sx={{ mb: 2, border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#f8fafc' }}>
                                    <Skeleton variant="circular" width={36} height={36} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton width={140} height={18} />
                                        <Skeleton width={90} height={14} />
                                    </Box>
                                </Box>
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Skeleton width="60%" height={16} />
                                    <Skeleton width="40%" height={14} sx={{ mt: 0.5 }} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Error */}
                {!loading && error && (
                    <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
                        <Typography color="error" mb={1}>{error}</Typography>
                        <Button size="small" onClick={load}>Reintentar</Button>
                    </Box>
                )}

                {/* Empty state */}
                {!loading && !error && groups.length === 0 && (
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', height: '100%', gap: 2, color: 'text.secondary', mt: 8,
                    }}>
                        <SupervisorAccountIcon sx={{ fontSize: 56, color: '#007AFF', opacity: 0.3 }} />
                        <Typography variant="h6" fontWeight={600} color="text.secondary">
                            Sin conversaciones activas
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            No hay agentes con conversaciones en control humano en este momento.
                        </Typography>
                    </Box>
                )}

                {/* Grupos por agente */}
                {!loading && !error && groups.map(g => (
                    <AgentGroup
                        key={g.agentUserId}
                        agentName={g.agentName}
                        agentUserId={g.agentUserId}
                        entries={g.entries}
                        isSuperAdmin={isSuperAdmin}
                        onVer={handleVer}
                        onLiberar={(entry) => setConfirmEntry(entry)}
                    />
                ))}
            </Box>
            )}

            {/* Dialog confirmación liberar */}
            <Dialog open={!!confirmEntry} onClose={() => setConfirmEntry(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Liberar conversación</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        ¿Confirmas liberar la asignación de{' '}
                        <strong>{confirmEntry?.agentName}</strong> con{' '}
                        <strong>{confirmEntry?.clientName || 'este cliente'}</strong>?
                        El agente ya no tendrá esta conversación asignada.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmEntry(null)} sx={{ textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleLiberar}
                        color="error"
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                    >
                        Liberar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SupervisionPage;
